import { Shell } from '@/components/Shell';
import { AvailabilityCalendar } from '@/components/fleet/AvailabilityCalendar';
import { PhotosPanel } from '@/components/fleet/PhotosPanel';
import { ConfirmButton } from '@/components/ui/ConfirmButton';
import { Pill, stateLabel, stateToTone } from '@/components/ui/Pill';
import { ApiError, type VehicleDetail, adminApi } from '@/lib/api';
import { photoUrl } from '@levamar/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';

const S3_PUBLIC =
  (import.meta.env.VITE_S3_PUBLIC_URL as string | undefined) ?? 'http://localhost:9000/levamar-dev';

export const Route = createFileRoute('/fleet/$id')({
  beforeLoad: async () => {
    try {
      const me = await adminApi.me();
      if (me.role !== 'admin') throw redirect({ to: '/login' });
    } catch {
      throw redirect({ to: '/login' });
    }
  },
  component: VehicleDetailPage,
});

type Tab = 'info' | 'photos' | 'calendar' | 'bookings';

const TABS: { key: Tab; label: string }[] = [
  { key: 'info', label: 'INFO & PREÇO' },
  { key: 'photos', label: 'GALERIA' },
  { key: 'calendar', label: 'CALENDÁRIO' },
  { key: 'bookings', label: 'HISTÓRICO' },
];

function VehicleDetailPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('info');
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'vehicle', id],
    queryFn: () => adminApi.vehicleDetail(id),
  });

  if (isLoading || !data) {
    return (
      <Shell page="…" sub="VIATURA · A CARREGAR">
        <div style={{ padding: 24, color: 'var(--lv-gray-500)' }}>A carregar…</div>
      </Shell>
    );
  }

  return (
    <Shell page={data.model.nameLocale.toUpperCase()} sub={`VIATURA · ${data.plate}`}>
      <Hero vehicle={data} />

      {/* Tab strip */}
      <nav
        style={{
          marginTop: 20,
          display: 'flex',
          gap: 0,
          borderBottom: '1px solid var(--lv-line)',
        }}
      >
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            style={{
              background: tab === t.key ? 'var(--lv-brand)' : 'transparent',
              color: tab === t.key ? '#fff' : 'var(--lv-ink)',
              border: 0,
              borderBottom: tab === t.key ? '2px solid var(--lv-brand)' : '2px solid transparent',
              padding: '10px 18px',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              cursor: 'pointer',
              transition: 'background 0.1s, color 0.1s',
            }}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <div style={{ marginTop: 16 }}>
        {tab === 'info' && (
          <InfoForm
            vehicle={data}
            onSave={() => qc.invalidateQueries({ queryKey: ['admin', 'vehicle', id] })}
            onDelete={() => {
              qc.invalidateQueries({ queryKey: ['admin', 'vehicles'] });
              navigate({ to: '/fleet' });
            }}
          />
        )}
        {tab === 'photos' && (
          <PhotosPanel
            vehicleId={id}
            photos={data.photos}
            onChange={() => qc.invalidateQueries({ queryKey: ['admin', 'vehicle', id] })}
          />
        )}
        {tab === 'calendar' && <AvailabilityCalendar vehicleId={id} />}
        {tab === 'bookings' && <BookingsSection bookings={data.bookings} />}
      </div>
    </Shell>
  );
}

function Hero({ vehicle }: { vehicle: VehicleDetail }) {
  const cover = [...vehicle.photos].sort((a, b) => a.position - b.position)[0];
  const coverUrl = cover ? photoUrl(S3_PUBLIC, cover, 1280) : null;
  const accent = vehicle.model.defaultColor ?? vehicle.color ?? '#234948';

  const fuelLabel: Record<string, string> = {
    gasoline: 'Gasolina',
    diesel: 'Diesel',
    hybrid: 'Híbrido',
  };

  return (
    <section
      style={{
        display: 'grid',
        gridTemplateColumns: '480px 1fr',
        background: '#fff',
        border: '1px solid var(--lv-line)',
        overflow: 'hidden',
      }}
    >
      {/* Photo */}
      <div
        style={{
          aspectRatio: '4 / 3',
          background: coverUrl ? `url(${coverUrl}) center / cover no-repeat ${accent}` : accent,
          position: 'relative',
          minHeight: 260,
        }}
      >
        {!coverUrl && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'rgba(255,255,255,.45)',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <svg viewBox="0 0 360 160" width={200} height={90} fill="none" opacity={0.4} aria-hidden="true">
              <path
                d="M20 116 L32 84 Q44 64 76 56 L112 38 Q128 24 152 24 L240 24 Q256 24 268 36 L292 60 Q330 64 338 88 L338 120 Z"
                fill="#fff"
              />
            </svg>
            <span>Sem foto de capa</span>
          </div>
        )}
        <div
          style={{
            position: 'absolute',
            top: 12,
            left: 12,
            background: 'rgba(0,0,0,.55)',
            color: '#fff',
            padding: '3px 8px',
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
          }}
        >
          {vehicle.photos.length} foto{vehicle.photos.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Info panel */}
      <div
        style={{
          padding: '28px 28px 24px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        <div>
          {/* Status pill */}
          <Pill label={stateLabel(vehicle.status)} tone={stateToTone(vehicle.status)} />

          {/* Model name */}
          <div className="display" style={{ fontSize: 48, lineHeight: 1, marginTop: 12 }}>
            {vehicle.model.nameLocale}
          </div>

          <div className="h-rule" style={{ marginTop: 10 }} />

          {/* Spec line */}
          <div style={{ marginTop: 12, fontSize: 13, color: 'var(--lv-gray-500)', lineHeight: 1.6 }}>
            {vehicle.year} · {vehicle.model.bodyType.toUpperCase()} · {vehicle.model.seats} lugares ·{' '}
            {vehicle.model.transmission === 'AT' ? 'Automática' : 'Manual'} ·{' '}
            {fuelLabel[vehicle.model.fuel] ?? vehicle.model.fuel}
          </div>
        </div>

        {/* Stats grid */}
        <dl
          style={{
            marginTop: 20,
            display: 'grid',
            gridTemplateColumns: 'repeat(4, auto)',
            gap: '14px 28px',
          }}
        >
          <Stat label="Matrícula" value={vehicle.plate} mono />
          <Stat label="Quilómetros" value={vehicle.km.toLocaleString('pt-PT')} />
          <Stat label="VIN" value={vehicle.vin ?? '—'} mono />
          <Stat label="Cor (hex)" value={vehicle.color ?? '—'} />
          <Stat label="EUR / dia" value={`${vehicle.pricing.eurPerDay.toFixed(0)} €`} />
          <Stat label="CVE / dia" value={`${vehicle.pricing.cvePerDay.toFixed(0)} CVE`} />
          <Stat label="Caução EUR" value={`${vehicle.pricing.eurCaucao.toFixed(0)} €`} />
          <Stat label="Desc. semanal" value={`${vehicle.pricing.weeklyDiscountPct}%`} />
        </dl>
      </div>
    </section>
  );
}

function Stat({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div>
      <dt
        style={{
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: 'var(--lv-gray-500)',
        }}
      >
        {label}
      </dt>
      <dd
        style={{
          margin: 0,
          marginTop: 3,
          fontSize: 14,
          fontWeight: 700,
          fontFamily: mono ? 'var(--lv-font-mono)' : undefined,
        }}
      >
        {value}
      </dd>
    </div>
  );
}

function BookingsSection({ bookings }: { bookings: VehicleDetail['bookings'] }) {
  const dateFmt = new Intl.DateTimeFormat('pt-PT', { dateStyle: 'medium' });

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
      <BookingList title="Próximas" empty="Sem reservas futuras." items={bookings.upcoming} fmt={dateFmt} />
      <BookingList title="Histórico" empty="Sem reservas passadas." items={bookings.recent} fmt={dateFmt} />
    </div>
  );
}

function BookingList({
  title,
  empty,
  items,
  fmt,
}: {
  title: string;
  empty: string;
  items: { id: string; code: string; state: string; fromAt: string; toAt: string }[];
  fmt: Intl.DateTimeFormat;
}) {
  return (
    <section
      style={{
        background: '#fff',
        border: '1px solid var(--lv-line)',
        padding: 20,
      }}
    >
      <div className="label" style={{ color: 'var(--lv-brand)' }}>
        {title.toUpperCase()}
      </div>
      {items.length === 0 ? (
        <p style={{ marginTop: 12, fontSize: 12, color: 'var(--lv-gray-500)' }}>{empty}</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: '12px 0 0', display: 'grid', gap: 6 }}>
          {items.map((b) => (
            <li key={b.id}>
              <Link
                to="/bookings/$id"
                params={{ id: b.id }}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '130px 1fr auto',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 12px',
                  border: '1px solid var(--lv-line)',
                  background: 'var(--lv-surface-soft)',
                  textDecoration: 'none',
                  color: 'inherit',
                  fontSize: 13,
                }}
              >
                <span
                  style={{
                    fontFamily: 'var(--lv-font-mono)',
                    fontWeight: 700,
                    color: 'var(--lv-brand)',
                    fontSize: 12,
                  }}
                >
                  {b.code}
                </span>
                <span style={{ fontSize: 11, color: 'var(--lv-gray-500)' }}>
                  {fmt.format(new Date(b.fromAt))} → {fmt.format(new Date(b.toAt))}
                </span>
                <Pill label={stateLabel(b.state)} tone={stateToTone(b.state)} />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function InfoForm({
  vehicle,
  onSave,
  onDelete,
}: {
  vehicle: VehicleDetail;
  onSave: () => void;
  onDelete: () => void;
}) {
  const [form, setForm] = useState({
    plate: vehicle.plate,
    year: vehicle.year,
    color: vehicle.color ?? '',
    vin: vehicle.vin ?? '',
    km: vehicle.km,
    status: vehicle.status,
    showOnHome: vehicle.showOnHome,
    eurPerDay: vehicle.pricing.eurPerDay,
    cvePerDay: vehicle.pricing.cvePerDay,
    eurCaucao: vehicle.pricing.eurCaucao,
    cveCaucao: vehicle.pricing.cveCaucao,
    weeklyDiscountPct: vehicle.pricing.weeklyDiscountPct,
  });
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    if (savedAt === null) return;
    const t = setTimeout(() => setSavedAt(null), 2400);
    return () => clearTimeout(t);
  }, [savedAt]);

  // Ficha técnica lives on a different entity (the MODEL, not this vehicle), so
  // it needs its own PATCH — but not its own button. The section registers its
  // save here and the single "Guardar alterações" fires both.
  const saveSpecsRef = useRef<(() => Promise<unknown>) | null>(null);

  const save = useMutation({
    mutationFn: () =>
      Promise.all([
        adminApi.patchVehicle(vehicle.id, {
          plate: form.plate,
          year: form.year,
          color: form.color || null,
          vin: form.vin || null,
          km: form.km,
          status: form.status,
          showOnHome: form.showOnHome,
          pricing: {
            eurPerDay: form.eurPerDay,
            cvePerDay: form.cvePerDay,
            eurCaucao: form.eurCaucao,
            cveCaucao: form.cveCaucao,
            weeklyDiscountPct: form.weeklyDiscountPct,
          },
        }),
        saveSpecsRef.current?.(),
      ]),
    onSuccess: () => {
      setSavedAt(Date.now());
      onSave();
    },
  });

  const remove = useMutation({
    mutationFn: () => adminApi.deleteVehicle(vehicle.id),
    onSuccess: onDelete,
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
      style={{ display: 'grid', gap: 16 }}
    >
      {/* Identidade */}
      <div style={{ background: '#fff', border: '1px solid var(--lv-line)', padding: 24 }}>
        <div className="label" style={{ color: 'var(--lv-brand)', marginBottom: 16 }}>
          IDENTIDADE
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <Field label="Matrícula">
            <input
              type="text"
              value={form.plate}
              onChange={(e) => setForm({ ...form, plate: e.target.value.toUpperCase() })}
              style={input}
            />
          </Field>
          <Field label="Ano">
            <input
              type="number"
              value={form.year}
              onChange={(e) => setForm({ ...form, year: Number(e.target.value) })}
              style={input}
            />
          </Field>
          <Field label="Estado">
            <select
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value as VehicleDetail['status'] })}
              style={input}
            >
              <option value="draft">Rascunho</option>
              <option value="active">Activa</option>
              <option value="maintenance">Manutenção</option>
              <option value="retired">Retirada</option>
            </select>
          </Field>
          <Field label="Cor (hex)">
            <input
              type="text"
              value={form.color}
              onChange={(e) => setForm({ ...form, color: e.target.value })}
              placeholder="#234948"
              style={input}
            />
          </Field>
          <Field label="VIN">
            <input
              type="text"
              value={form.vin}
              onChange={(e) => setForm({ ...form, vin: e.target.value })}
              style={{ ...input, fontFamily: 'var(--lv-font-mono)' }}
            />
          </Field>
          <Field label="Quilómetros">
            <input
              type="number"
              value={form.km}
              onChange={(e) => setForm({ ...form, km: Number(e.target.value) })}
              min={0}
              style={input}
            />
          </Field>
          <Field label="Página inicial">
            <select
              value={form.showOnHome ? 'yes' : 'no'}
              onChange={(e) => setForm({ ...form, showOnHome: e.target.value === 'yes' })}
              style={input}
            >
              <option value="no">Não mostrar</option>
              <option value="yes">Mostrar (máx. 2)</option>
            </select>
          </Field>
        </div>
      </div>

      {/* Preço */}
      <div style={{ background: '#fff', border: '1px solid var(--lv-line)', padding: 24 }}>
        <div className="label" style={{ color: 'var(--lv-brand)', marginBottom: 16 }}>
          PREÇO · CAUÇÃO
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12 }}>
          <Field label="EUR / dia">
            <input
              type="number"
              value={form.eurPerDay}
              onChange={(e) => setForm({ ...form, eurPerDay: Number(e.target.value) })}
              style={input}
              min={1}
              step={1}
            />
          </Field>
          <Field label="CVE / dia">
            <input
              type="number"
              value={form.cvePerDay}
              onChange={(e) => setForm({ ...form, cvePerDay: Number(e.target.value) })}
              style={input}
              min={1}
              step={1}
            />
          </Field>
          <Field label="Caução EUR">
            <input
              type="number"
              value={form.eurCaucao}
              onChange={(e) => setForm({ ...form, eurCaucao: Number(e.target.value) })}
              style={input}
              min={0}
              step={1}
            />
          </Field>
          <Field label="Caução CVE">
            <input
              type="number"
              value={form.cveCaucao}
              onChange={(e) => setForm({ ...form, cveCaucao: Number(e.target.value) })}
              style={input}
              min={0}
              step={1}
            />
          </Field>
          <Field label="Desc. semanal %">
            <input
              type="number"
              value={form.weeklyDiscountPct}
              onChange={(e) => setForm({ ...form, weeklyDiscountPct: Number(e.target.value) })}
              style={input}
              min={0}
              max={50}
              step={1}
            />
          </Field>
        </div>
      </div>

      {/* Ficha técnica */}
      <TechnicalSpecsSection modelId={vehicle.model.id} saveRef={saveSpecsRef} />

      {/* Actions */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 16,
        }}
      >
        <div>
          {save.error instanceof ApiError && save.error.code === 'home_limit' && (
            <span style={{ color: 'var(--lv-err)', fontSize: 13, fontWeight: 600 }}>
              Já há 2 viaturas na página inicial. Retire uma antes de adicionar esta.
            </span>
          )}
          {savedAt && (
            <span
              style={{
                color: 'var(--lv-ok)',
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
              }}
            >
              ✓ GUARDADO
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <ConfirmButton
            label="Eliminar"
            question="Eliminar viatura? Mantém o histórico de reservas."
            onConfirm={() => remove.mutate()}
            disabled={remove.isPending}
            style={btnDanger}
          />
          <button type="submit" disabled={save.isPending} style={btnPrimary}>
            {save.isPending ? '…' : 'Guardar alterações'}
          </button>
        </div>
      </div>
    </form>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block' }}>
      <span
        style={{
          display: 'block',
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'var(--lv-gray-500)',
          marginBottom: 5,
        }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}

/**
 * FICHA TÉCNICA — PATCHes /admin/models/:modelId, a different entity from the
 * vehicle, so it keeps its own request and its own field state. It does NOT
 * keep its own button: it publishes `save` through `saveRef` and the parent
 * form's single "Guardar alterações" runs both PATCHes together.
 */
function TechnicalSpecsSection({
  modelId,
  saveRef,
}: {
  modelId: string;
  saveRef: React.MutableRefObject<(() => Promise<unknown>) | null>;
}) {
  const qc = useQueryClient();
  const [specs, setSpecs] = useState({
    doors: '' as string,
    engineCc: '' as string,
    engineHp: '' as string,
    consumptionL100km: '' as string,
    luggageLarge: '' as string,
    luggageSmall: '' as string,
    acType: '' as string,
    multimedia: '' as string,
    // Feature 002 D6/D7
    drivetrain: '' as string,
    unlimitedKm: '' as string, // '' | 'yes' | 'no'
    tripStyles: '' as string, // comma-separated tags
    // Per-car flow: 'no' = quote-only (WhatsApp), 'yes' = online booking
    bookingEnabled: 'no' as string,
  });
  const initialized = useRef(false);

  const modelQuery = useQuery({
    queryKey: ['admin', 'model-specs', modelId],
    queryFn: async () => {
      const { items } = await adminApi.models();
      return items.find((i) => i.id === modelId) ?? null;
    },
  });

  useEffect(() => {
    const m = modelQuery.data;
    if (!m || initialized.current) return;
    initialized.current = true;
    setSpecs({
      doors: m.doors != null ? String(m.doors) : '',
      engineCc: m.engineCc != null ? String(m.engineCc) : '',
      engineHp: m.engineHp != null ? String(m.engineHp) : '',
      consumptionL100km: m.consumptionL100km != null ? String(m.consumptionL100km) : '',
      luggageLarge: m.luggageLarge != null ? String(m.luggageLarge) : '',
      luggageSmall: m.luggageSmall != null ? String(m.luggageSmall) : '',
      acType: m.acType ?? '',
      multimedia: m.multimedia ?? '',
      drivetrain: m.drivetrain ?? '',
      unlimitedKm: m.unlimitedKm == null ? '' : m.unlimitedKm ? 'yes' : 'no',
      tripStyles: (m.tripStyles ?? []).join(', '),
      bookingEnabled: m.bookingEnabled ? 'yes' : 'no',
    });
  }, [modelQuery.data]);

  const save = useMutation({
    mutationFn: () =>
      adminApi.patchModel(modelId, {
        doors: specs.doors !== '' ? Number(specs.doors) : null,
        engineCc: specs.engineCc !== '' ? Number(specs.engineCc) : null,
        engineHp: specs.engineHp !== '' ? Number(specs.engineHp) : null,
        consumptionL100km: specs.consumptionL100km !== '' ? Number(specs.consumptionL100km) : null,
        luggageLarge: specs.luggageLarge !== '' ? Number(specs.luggageLarge) : null,
        luggageSmall: specs.luggageSmall !== '' ? Number(specs.luggageSmall) : null,
        acType: specs.acType !== '' ? specs.acType : null,
        multimedia: specs.multimedia !== '' ? specs.multimedia : null,
        drivetrain: specs.drivetrain !== '' ? specs.drivetrain : null,
        unlimitedKm: specs.unlimitedKm === '' ? null : specs.unlimitedKm === 'yes',
        bookingEnabled: specs.bookingEnabled === 'yes',
        tripStyles:
          specs.tripStyles.trim() === ''
            ? null
            : specs.tripStyles
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean),
      }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['admin', 'model-specs', modelId] });
      const m = res.model;
      if (m) {
        setSpecs({
          doors: m.doors != null ? String(m.doors) : '',
          engineCc: m.engineCc != null ? String(m.engineCc) : '',
          engineHp: m.engineHp != null ? String(m.engineHp) : '',
          consumptionL100km: m.consumptionL100km != null ? String(m.consumptionL100km) : '',
          luggageLarge: m.luggageLarge != null ? String(m.luggageLarge) : '',
          luggageSmall: m.luggageSmall != null ? String(m.luggageSmall) : '',
          acType: m.acType ?? '',
          multimedia: m.multimedia ?? '',
          drivetrain: m.drivetrain ?? '',
          unlimitedKm: m.unlimitedKm == null ? '' : m.unlimitedKm ? 'yes' : 'no',
          tripStyles: (m.tripStyles ?? []).join(', '),
          bookingEnabled: m.bookingEnabled ? 'yes' : 'no',
        });
      }
    },
  });

  function num(field: keyof typeof specs) {
    return (e: React.ChangeEvent<HTMLInputElement>) => setSpecs({ ...specs, [field]: e.target.value });
  }

  // Publish the save to the parent form. Re-assigned on every render so it
  // always closes over the CURRENT `specs`, and cleared on unmount so a form
  // without this section can't call into a dead component.
  //
  // Gated on `modelQuery.data`: until the model loads, `specs` is all empty
  // strings, which this PATCH maps to nulls. With the old dedicated button the
  // user had to click it deliberately; now that "Guardar alterações" fires it
  // too, an unguarded ref would silently wipe every spec field on a save made
  // while the model was still loading (or had failed to load).
  saveRef.current = modelQuery.data ? () => save.mutateAsync() : null;
  useEffect(() => {
    return () => {
      saveRef.current = null;
    };
  }, [saveRef]);

  return (
    <div style={{ background: '#fff', border: '1px solid var(--lv-line)', padding: 24 }}>
      <div className="label" style={{ color: 'var(--lv-brand)', marginBottom: 16 }}>
        FICHA TÉCNICA
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 12 }}>
        <Field label="Portas">
          <input
            type="number"
            value={specs.doors}
            onChange={num('doors')}
            min={2}
            max={7}
            style={input}
            placeholder="5"
          />
        </Field>
        <Field label="Cilindrada (cc)">
          <input
            type="number"
            value={specs.engineCc}
            onChange={num('engineCc')}
            min={500}
            style={input}
            placeholder="1600"
          />
        </Field>
        <Field label="Potência (cv)">
          <input
            type="number"
            value={specs.engineHp}
            onChange={num('engineHp')}
            min={1}
            style={input}
            placeholder="113"
          />
        </Field>
        <Field label="Consumo (L/100km)">
          <input
            type="number"
            value={specs.consumptionL100km}
            onChange={num('consumptionL100km')}
            step={0.1}
            min={0}
            style={input}
            placeholder="6.2"
          />
        </Field>
        <Field label="Malas grandes">
          <input
            type="number"
            value={specs.luggageLarge}
            onChange={num('luggageLarge')}
            min={0}
            style={input}
            placeholder="2"
          />
        </Field>
        <Field label="Malas pequenas">
          <input
            type="number"
            value={specs.luggageSmall}
            onChange={num('luggageSmall')}
            min={0}
            style={input}
            placeholder="1"
          />
        </Field>
        <Field label="Ar condicionado">
          <select
            value={specs.acType}
            onChange={(e) => setSpecs({ ...specs, acType: e.target.value })}
            style={input}
          >
            <option value="">Não especificado</option>
            <option value="manual">Manual</option>
            <option value="climate">Climatronic</option>
            <option value="dual">Dual Zone</option>
          </select>
        </Field>
        <Field label="Multimédia">
          <select
            value={specs.multimedia}
            onChange={(e) => setSpecs({ ...specs, multimedia: e.target.value })}
            style={input}
          >
            <option value="">Não especificado</option>
            <option value="bt_usb">BT + USB</option>
            <option value="apple_carplay">Apple CarPlay</option>
            <option value="android_auto">Android Auto</option>
            <option value="all">CarPlay + Android</option>
          </select>
        </Field>
        {/* Feature 002 D7 — drivetrain + unlimited km */}
        <Field label="Tração">
          <select
            value={specs.drivetrain}
            onChange={(e) => setSpecs({ ...specs, drivetrain: e.target.value })}
            style={input}
          >
            <option value="">Não especificado</option>
            <option value="fwd">FWD</option>
            <option value="rwd">RWD</option>
            <option value="awd">AWD</option>
            <option value="4x4">4×4</option>
          </select>
        </Field>
        <Field label="Km ilimitados">
          <select
            value={specs.unlimitedKm}
            onChange={(e) => setSpecs({ ...specs, unlimitedKm: e.target.value })}
            style={input}
          >
            <option value="">Não especificado</option>
            <option value="yes">Sim</option>
            <option value="no">Não</option>
          </select>
        </Field>
        {/* Per-car flow: quote-only (WhatsApp) vs online booking. Off until SISP is live. */}
        <Field label="Reservas online">
          <select
            value={specs.bookingEnabled}
            onChange={(e) => setSpecs({ ...specs, bookingEnabled: e.target.value })}
            style={input}
          >
            <option value="no">Não — pedir orçamento (WhatsApp)</option>
            <option value="yes">Sim — reserva online</option>
          </select>
        </Field>
        {/* Feature 002 D6 — trip-style tags */}
        <Field label="Trip-styles (separados por vírgula)">
          <input
            type="text"
            value={specs.tripStyles}
            onChange={(e) => setSpecs({ ...specs, tripStyles: e.target.value })}
            style={input}
            placeholder="beach_4x4, family_7"
          />
        </Field>
      </div>
    </div>
  );
}

const input: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid var(--lv-line)',
  background: '#fff',
  fontSize: 13,
  borderRadius: 0,
};
const btnPrimary: React.CSSProperties = {
  background: 'var(--lv-brand)',
  color: '#fff',
  border: 0,
  padding: '11px 22px',
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  cursor: 'pointer',
};
const btnDanger: React.CSSProperties = {
  background: '#fff',
  color: 'var(--lv-err)',
  border: '1px solid var(--lv-err)',
  padding: '11px 22px',
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  cursor: 'pointer',
};
